import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import Cookies from "js-cookie";
import { Box, Card, CardContent, CircularProgress, Grid, Tab, Tabs, TextField, Typography } from "@mui/material";
import { PiQrCodeLight } from "react-icons/pi";
import { MdKeyboard, MdQrCodeScanner } from "react-icons/md";
import { API_URL } from "../../utils/constants";
import { cssProperties } from "../../utils/commonCssProperties";
import MauvelousPrimaryButton from "../../common/button/mauvelousPrimaryButton";
import { rememberVerificationUrl } from "../../utils/returnUrl";
import { persistor } from "../../redux/store";
import { DATA_VERIFIER_ROLE } from "../../utils/roles";
import AdmitCardQrScanner from "./QrScanner";
import { SCAN_TYPE } from "./parseScannedQr";

// States handled: idle (scanner / roll-number search, nothing searched yet) | loading |
// success | tampered (QR failed the integrity check) | unauthorized (403) |
// error (not found / network / anything else, with a message)
const STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  TAMPERED: "tampered",
  UNAUTHORIZED: "unauthorized",
  ERROR: "error",
};

const GENERIC_ERROR = "Something went wrong. Please try again.";

// Lets a MauvelousPrimaryButton (which takes no sx) span the row on phones.
const fullWidthButtonOnPhone = { "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } };

// The backend answers with HTTP 200 and the real outcome in the body.
const resolveResponse = (body) => {
  if (body?.statusCode === 200 && body?.result) {
    return { status: STATUS.SUCCESS, result: body.result };
  }
  if (body?.statusCode === 403) {
    return { status: STATUS.UNAUTHORIZED };
  }
  if (body?.errorCode === "TAMPERED") {
    return { status: STATUS.TAMPERED };
  }
  return { status: STATUS.ERROR, message: body?.message || GENERIC_ERROR };
};

const authHeaders = () => ({
  authorization: `Bearer ${Cookies.get("mhet_mst_token")}`,
});

// backofficeAuthorization's reply when the login token is missing or expired.
const isLoginExpired = (body) =>
  body?.statusCode === 400 && body?.message === "Authorization Failed";

// The saved login is no longer valid: clear it the same way logout does, keep
// this link, and send the verifier to log in again. They come straight back here.
const redirectToLogin = () => {
  rememberVerificationUrl();
  Cookies.remove("mhet_mst_token");
  Cookies.remove("mhet_mst_userid");
  persistor.purge().finally(() => {
    window.location.href = `/${Cookies.get("mhet_mst_project")}/userlogin`;
  });
};

// How a verifier looks a candidate up when no QR link was opened.
const MODE = {
  SCAN: "scan",
  ROLL_NO: "rollno",
};

// Verifies a candidate for a Data Verifier, either from a scanned admit-card QR
// (the `data` param is present) or, without one, by scanning the QR here or by
// the roll number printed on the admit card.
export default function DataVerifier() {
  const [searchParams, setSearchParams] = useSearchParams();
  const data = searchParams.get("data");

  const { backofficeTokenDetails } = useSelector((state) => state.authReducer);
  const roles = backofficeTokenDetails?.role?.role;
  const rolesLoaded = Array.isArray(roles);
  const isDataVerifier = rolesLoaded && roles.includes(DATA_VERIFIER_ROLE);

  const [status, setStatus] = useState(data ? STATUS.LOADING : STATUS.IDLE);
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState(MODE.SCAN);
  const [rollNo, setRollNo] = useState("");
  // Bumped on every search and every edit, so only the latest search's answer
  // is ever shown — never a slower, earlier one for a different roll number.
  const searchSeq = useRef(0);

  // A page restored from the browser's back/forward cache (e.g. Back after
  // logout) would show the last candidate again; reload so access is re-checked.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) window.location.reload();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const applyResponse = (body) => {
    if (isLoginExpired(body)) {
      setStatus(STATUS.LOADING);
      redirectToLogin();
      return;
    }
    const resolved = resolveResponse(body);
    setResult(resolved.result || null);
    setMessage(resolved.message || "");
    setStatus(resolved.status);
  };

  useEffect(() => {
    // Without scanned data the page is in roll-number search mode.
    if (!data || !isDataVerifier) return;

    let isMounted = true;
    setStatus(STATUS.LOADING);

    axios
      .get(`${API_URL}/backoffice/candidate-verification`, {
        params: { data },
        headers: authHeaders(),
      })
      .then((res) => {
        if (isMounted) applyResponse(res?.data);
      })
      .catch(() => {
        if (isMounted) applyResponse({ message: "Something went wrong. Please scan the QR code again." });
      });

    return () => {
      isMounted = false;
    };
  }, [data, isDataVerifier]);

  const searchRollNo = (rawValue) => {
    const seq = ++searchSeq.current;
    const value = rawValue.trim();
    setResult(null);
    if (!value) {
      setMessage("Please enter a roll number");
      setStatus(STATUS.ERROR);
      return;
    }

    setStatus(STATUS.LOADING);
    axios
      .get(`${API_URL}/backoffice/candidate-verification/rollno/${encodeURIComponent(value)}`, {
        headers: authHeaders(),
      })
      .then((res) => {
        if (seq === searchSeq.current) applyResponse(res?.data);
      })
      .catch(() => {
        if (seq === searchSeq.current) applyResponse({ message: GENERIC_ERROR });
      });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchRollNo(rollNo);
  };

  // A scanned verification link opens this page with its `data`, exactly as if
  // the link had been opened from a camera app (and Back returns to the
  // scanner). A QR that only carries the roll number is looked up by it.
  const handleScan = (scan) => {
    if (scan.type === SCAN_TYPE.SEALED) {
      setSearchParams({ data: scan.value });
      return;
    }
    setMode(MODE.ROLL_NO);
    setRollNo(scan.value);
    searchRollNo(scan.value);
  };

  // Back to an empty page, scanner open, for the next candidate.
  const handleScanNext = () => {
    setSearchParams({});
  };

  const handleModeChange = (e, value) => {
    searchSeq.current += 1;
    setMode(value);
    setResult(null);
    setMessage("");
    setStatus(STATUS.IDLE);
  };

  // Editing the roll number hides the previous result, so the details on
  // screen always belong to the roll number in the box.
  const handleRollNoChange = (e) => {
    setRollNo(e.target.value);
    searchSeq.current += 1;
    setResult(null);
    setMessage("");
    setStatus(STATUS.IDLE);
  };

  const renderMessageCard = (title, subtitle) => (
    <Grid container justifyContent="center" sx={{ marginTop: "12px" }}>
      <Grid item xs={12} sm={8} md={6}>
        <Card
          sx={{
            borderRadius: "8px",
            boxShadow: "none",
            border: `1px solid ${cssProperties?.bordercolor?.secondary}`,
            padding: { xs: "24px 16px", sm: "32px" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            textAlign: "center",
          }}
        >
          <PiQrCodeLight size={48} color={cssProperties?.color?.failure} />
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              color: `${cssProperties?.fontcolor?.tableblack}`,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: `${cssProperties?.color?.textsecondary}`,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Card>
      </Grid>
    </Grid>
  );

  const renderLoader = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "64px",
      }}
    >
      <CircularProgress sx={{ color: `${cssProperties?.color?.primary}` }} />
    </Box>
  );

  const displayValue = (value) =>
    value === null || value === undefined || value === "" ? "-" : String(value);

  // One label/value pair of the details card.
  const renderField = (label, value, valueSx = {}) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: { xs: 11, sm: 12 },
          fontWeight: 500,
          lineHeight: "16px",
          color: `${cssProperties?.color?.textsecondary}`,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: 13, sm: 14 },
          fontWeight: 600,
          lineHeight: "18px",
          color: `${cssProperties?.fontcolor?.tableblack}`,
          overflowWrap: "anywhere",
          ...valueSx,
        }}
      >
        {displayValue(value)}
      </Typography>
    </Box>
  );

  const renderPhoto = () => (
    <Box
      sx={{
        width: { xs: "84px", sm: "110px" },
        height: { xs: "105px", sm: "138px" },
        border: `1px solid ${cssProperties?.bordercolor?.secondary}`,
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {result?.photo ? (
        <img
          src={result.photo}
          alt="Candidate"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 500,
            color: `${cssProperties?.color?.textsecondary}`,
            textAlign: "center",
            padding: "6px",
          }}
        >
          Photo not available
        </Typography>
      )}
    </Box>
  );

  // Laid out to fit a phone screen without scrolling: who the candidate is
  // beside the photo, then the exam slot, then course and venue.
  const renderDetails = () => (
    <Grid container justifyContent="center" sx={{ marginTop: "12px" }}>
      <Grid item xs={12} sm={10} md={8} lg={6}>
        <Card
          sx={{
            borderRadius: "8px",
            boxShadow: "none",
            border: `1px solid ${cssProperties?.bordercolor?.secondary}`,
          }}
        >
          <CardContent sx={{ padding: { xs: "12px", sm: "20px" }, "&:last-child": { paddingBottom: { xs: "12px", sm: "20px" } } }}>
            <Typography
              sx={{
                fontSize: { xs: 15, sm: 16 },
                fontWeight: 700,
                lineHeight: "20px",
                color: `${cssProperties?.fontcolor?.primary}`,
              }}
            >
              Candidate Verification Details
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 11, sm: 12 },
                fontWeight: 500,
                color: `${cssProperties?.color?.textsecondary}`,
                marginBottom: { xs: "10px", sm: "16px" },
              }}
            >
              {data ? "Details from the admit card QR code" : "Details from the candidate record"}
            </Typography>

            <Box sx={{ display: "flex", gap: { xs: "12px", sm: "20px" }, alignItems: "flex-start" }}>
              {renderPhoto()}
              <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: { xs: "8px", sm: "12px" } }}>
                {renderField("Roll Number", result?.roll_no, { fontSize: { xs: 15, sm: 16 }, fontWeight: 700 })}
                {renderField("Application ID", result?.application_id)}
                {renderField("Gender", result?.gender)}
              </Box>
            </Box>

            <Box
              sx={{
                marginTop: { xs: "10px", sm: "16px" },
                display: "flex",
                flexDirection: "column",
                gap: { xs: "8px", sm: "12px" },
              }}
            >
              {renderField("Candidate Full Name (As per SSC/HSC)", result?.name_as_per_hsc, { fontWeight: 700 })}
              {renderField("Candidate Full Name (As per Aadhaar auto fetched)", result?.name_as_per_aadhaar, { fontWeight: 700 })}
            </Box>

            <Box
              sx={{
                marginTop: { xs: "10px", sm: "16px" },
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: { xs: "8px 12px", sm: "12px 16px" },
              }}
            >
              {renderField("Person with Disability (PWD)", result?.pwd)}
              {renderField("Scribe", result?.scribe)}
              {/* Scribe names only when the candidate has one. */}
              {result?.scribe1_name && renderField("Scribe 1 Name", result.scribe1_name)}
              {result?.scribe2_name && renderField("Scribe 2 Name", result.scribe2_name)}
            </Box>

            <Box
              sx={{
                marginTop: { xs: "12px", sm: "16px" },
                padding: { xs: "10px 12px", sm: "12px 16px" },
                borderRadius: "8px",
                backgroundColor: `${cssProperties?.surface?.primary}`,
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                gap: { xs: "8px 12px", sm: "12px 16px" },
              }}
            >
              {renderField("Exam Date", result?.exam_date)}
              {renderField("Exam Time", result?.time)}
              {renderField("Batch", result?.batch)}
              {renderField("Venue ID", result?.venue_id)}
            </Box>

            <Box
              sx={{
                marginTop: { xs: "10px", sm: "16px" },
                display: "flex",
                flexDirection: "column",
                gap: { xs: "8px", sm: "12px" },
              }}
            >
              {renderField("Name of the Course", result?.course_name)}
              {renderField("Venue Address", result?.venue_address, { fontWeight: 500 })}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderOutcome = () => {
    switch (status) {
      case STATUS.LOADING:
        return renderLoader();
      case STATUS.SUCCESS:
        return renderDetails();
      case STATUS.TAMPERED:
        // Nothing from the payload is shown once the integrity check fails.
        return renderMessageCard(
          "Data is Tampered",
          "This QR code could not be verified. The admit card may have been altered or may not be genuine."
        );
      case STATUS.UNAUTHORIZED:
        return renderMessageCard(
          "You are not authorized to view this",
          "Only users with the Data Verifier role can verify candidates."
        );
      case STATUS.ERROR:
        return renderMessageCard(message || GENERIC_ERROR);
      default:
        return null;
    }
  };

  // Token details load asynchronously after a refresh; wait before deciding.
  if (!rolesLoaded) {
    return renderLoader();
  }

  if (!isDataVerifier) {
    return renderMessageCard(
      "You are not authorized to view this",
      "Only users with the Data Verifier role can verify candidates."
    );
  }

  if (data) {
    return (
      <>
        {renderOutcome()}
        {status !== STATUS.LOADING && (
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: { xs: "12px", sm: "16px" }, ...fullWidthButtonOnPhone }}>
            <MauvelousPrimaryButton
              title="Scan Next Admit Card"
              icon={<MdQrCodeScanner />}
              handleClick={handleScanNext}
            />
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      <Grid container justifyContent="center" sx={{ marginTop: "12px" }}>
        <Grid item xs={12} sm={10} md={8} lg={6}>
          <Card
            sx={{
              borderRadius: "8px",
              boxShadow: "none",
              border: `1px solid ${cssProperties?.bordercolor?.secondary}`,
            }}
          >
            <Tabs
              value={mode}
              onChange={handleModeChange}
              variant="fullWidth"
              sx={{ borderBottom: `1px solid ${cssProperties?.bordercolor?.secondary}` }}
            >
              <Tab
                value={MODE.SCAN}
                label="Scan QR Code"
                icon={<MdQrCodeScanner size={18} />}
                iconPosition="start"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  minHeight: "48px",
                  minWidth: 0,
                  paddingX: { xs: "6px", sm: "16px" },
                  fontSize: { xs: 13, sm: 14 },
                  "& .MuiTab-iconWrapper": { marginRight: { xs: "4px", sm: "8px" } },
                }}
              />
              <Tab
                value={MODE.ROLL_NO}
                label="Enter Roll Number"
                icon={<MdKeyboard size={18} />}
                iconPosition="start"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  minHeight: "48px",
                  minWidth: 0,
                  paddingX: { xs: "6px", sm: "16px" },
                  fontSize: { xs: 13, sm: 14 },
                  "& .MuiTab-iconWrapper": { marginRight: { xs: "4px", sm: "8px" } },
                }}
              />
            </Tabs>
            <CardContent sx={{ padding: { xs: "16px", sm: "24px" } }}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: `${cssProperties?.fontcolor?.primary}`,
                  marginBottom: "4px",
                }}
              >
                {mode === MODE.SCAN ? "Verify by QR Code" : "Verify by Roll Number"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: `${cssProperties?.color?.textsecondary}`,
                  marginBottom: "16px",
                }}
              >
                {mode === MODE.SCAN
                  ? "Scan the QR code printed on the candidate's admit card."
                  : "Use this when the QR code on the admit card cannot be scanned."}
              </Typography>
              {mode === MODE.SCAN ? (
                // Unmounted on the roll-number tab, which also turns the camera off.
                <AdmitCardQrScanner onScan={handleScan} />
              ) : (
                <Box
                  component="form"
                  onSubmit={handleSearch}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: "12px",
                    alignItems: { xs: "stretch", sm: "center" },
                    ...fullWidthButtonOnPhone,
                  }}
                >
                  <TextField
                    size="small"
                    label="Roll Number"
                    value={rollNo}
                    onChange={handleRollNoChange}
                    inputProps={{ maxLength: 30 }}
                    sx={{ flex: { sm: "1 1 220px" } }}
                  />
                  <MauvelousPrimaryButton
                    type="submit"
                    title={status === STATUS.LOADING ? "loader" : "Search"}
                    disabled={status === STATUS.LOADING}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {status !== STATUS.LOADING && renderOutcome()}
    </>
  );
}
